import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, User, Cpu } from "lucide-react";

const human = {
  label: "Traditional Desk",
  points: ["1 call at a time", "Goes home at 6 PM", "Manual CRM entry", "Slow, inconsistent follow-up"],
};
const ai = {
  label: "S1EP Velocity",
  points: ["Infinite concurrency", "24 / 7 / 365 operation", "Instant CRM sync", "Sub-60-second follow-up, every time", "Omnichannel — auto-replies on 5 channels at once"],
};

export default function Advantage() {
  const [isAI, setIsAI] = useState(true);
  const active = isAI ? ai : human;

  return (
    <section id="advantage" className="relative py-24 md:py-32">
      <div className="container-x">
        <div className="mx-auto max-w-3xl text-center">
          <span className="eyebrow">The Unfair Advantage</span>
          <h2 className="mt-6 font-display text-4xl font-bold leading-[0.95] tracking-tight text-ink md:text-6xl">
            One switch. An <span className="grad-text">undeniable</span> gap.
          </h2>
          <p className="mt-5 text-lg text-ink-soft">Flip the toggle and watch the ceiling disappear.</p>
        </div>

        {/* toggle */}
        <div className="mt-12 flex justify-center">
          <div className="relative flex rounded-full border border-slate-200 bg-white p-1.5 shadow-card">
            <motion.div
              layout
              transition={{ type: "spring", stiffness: 400, damping: 32 }}
              className={`absolute inset-y-1.5 w-[calc(50%-6px)] rounded-full ${isAI ? "left-[calc(50%+0px)] bg-accent shadow-glow" : "left-1.5 bg-slate-300"}`}
            />
            <button
              data-testid="toggle-human"
              onClick={() => setIsAI(false)}
              className={`relative z-10 flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors md:px-8 ${!isAI ? "text-white" : "text-ink-muted"}`}
            >
              <User size={16} /> Traditional Desk
            </button>
            <button
              data-testid="toggle-velocity"
              onClick={() => setIsAI(true)}
              className={`relative z-10 flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors md:px-8 ${isAI ? "text-white" : "text-ink-muted"}`}
            >
              <Cpu size={16} /> S1EP Velocity
            </button>
          </div>
        </div>

        {/* morphing panel */}
        <motion.div
          animate={{
            background: isAI
              ? "linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)"
              : "linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)",
          }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto mt-10 max-w-4xl overflow-hidden rounded-[2rem] p-8 shadow-soft md:p-14"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={active.label}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex items-center gap-3">
                <span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${isAI ? "bg-white/20 text-white" : "bg-white text-ink-muted"}`}>
                  {isAI ? <Cpu size={24} /> : <User size={24} />}
                </span>
                <div>
                  <p className={`text-xs font-semibold uppercase tracking-widest ${isAI ? "text-white/70" : "text-ink-muted"}`}>Operating as</p>
                  <h3 className={`font-display text-3xl font-bold ${isAI ? "text-white" : "text-ink-soft"}`}>{active.label}</h3>
                </div>
              </div>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {active.points.map((p, i) => (
                  <motion.div
                    key={p}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 * i }}
                    className={`flex items-center gap-3 rounded-2xl p-4 ${isAI ? "bg-white/10" : "bg-white/70"}`}
                  >
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isAI ? "bg-white text-accent" : "bg-slate-300 text-slate-500"}`}>
                      {isAI ? <Check size={17} strokeWidth={3} /> : <X size={16} strokeWidth={3} />}
                    </span>
                    <span className={`font-semibold ${isAI ? "text-white" : "text-ink-muted line-through decoration-slate-400/60"}`}>{p}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
